import React, { useEffect, useState } from "react";
import NavBar from "@/components/navigation/NavBar";
import PreviewComponent from "./Preview";
import { z } from "zod";
import { useParams } from "react-router-dom";
import config from "@/config/config";
import SuccessModal from "@/components/Motion/SuccessModal";
import { AnimatePresence, motion } from "framer-motion";
import LoadingButton from "./LoadingButton";
import ButtonPrime from "@/components/navigation/ButtonPrime";
import useFormStore from "@/lib/FormZustand";
import CitySearchBox from "@/components/test/SearchBoxTest";
import useCoordinatesStore from "@/lib/coordinates-store";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import {Button } from "@/components/ui/button";
import { MapIcon } from "@heroicons/react/24/outline";
import { trackBewertungStart } from "@/lib/meta/pixel-tracker";


// Renders a group of radio buttons styled as cards.
const RadioCardGroup = ({
  title,
  options,
  name,
  selectedValue,
  onChange,
  error,
}) => (
  <div>
    {title && (
      <h2 className="text-xl text-prime-950 font-medium mb-6">{title}</h2>
    )}
    <div className="grid grid-cols-2 gap-4">
      {options.map(({ label, icon }) => (
        <label
          key={label}
          className={`group rounded-xl card p-4 min-h-[7rem] flex items-center justify-center cursor-pointer text-center transition-all ${
            selectedValue === label
              ? "border-none bg-prime-700/90 scale-[1.0] text-gray-200"
              : "bg-prime-50 hover:bg-prime-200/80 border-gray-200 text-prime-950"
          }`}
        >
          <input
            type="radio"
            name={name}
            value={label}
            checked={selectedValue === label}
            onChange={onChange}
            className="hidden"
          />
          {icon && <img src={icon} alt={label} className="w-12 h-12 mx-auto" />}
          <span className="mt-2  text-sm font-medium ">{label}</span>
        </label>
      ))}
    </div>
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
);

// Renders an input field with plus and minus buttons for incrementing/decrementing
const IncrementInput = ({
  label,
  name,
  value,
  onChange,
  error,
  handleIncrement,
  handleDecrement,
  placeholder = "",
}) => (
  <div className="">
    {label && <label className="block text-sm font-medium mb-1">{label}</label>}
    <div className="flex items-center">
      <button
        type="button"
        onClick={() => handleDecrement(name)}
        className="btn rounded-r-none bg-white border border-gray-300"
      >
        -
      </button>
      <input
        type="number"
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className="input w-full input-bordered border-gray-300 border-r-0 border-l-0 rounded-l-none rounded-r-none text-center bg-gray-100"
        style={{
          "-webkit-appearance": "none",
          margin: "0",
        }}
      />
      <button
        type="button"
        onClick={() => handleIncrement(name)}
        className="btn rounded-l-none bg-white border border-gray-300"
      >
        +
      </button>
    </div>
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
);

// Renders an input field with a right-aligned suffix (for example, m²)
const InputWithSuffix = ({
  label,
  name,
  value,
  onChange,
  error,
  placeholder,
  suffix,
}) => (
  <div>
    {label && <label className="block text-sm font-medium mb-1">{label}</label>}
    <div className="relative">
      <input
        type="number"
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className="input input-bordered input-md bg-gray-100 w-full pr-12"
      />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
        {suffix}
      </span>
    </div>
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
);

export default function FormBewerten() {
  const { formData, step, updateFormData, setStep, setStarted, started } = useFormStore();
  const { coordinates } = useCoordinatesStore();
  const totalSteps = 8;
  const [lastParam, setLastParam] = useState(null);
  const [errors, setErrors] = useState({});
  const [showModal, setShowModal] = useState(false);
  const { type } = useParams();
  const [contactData, setContactData] = useState({
    first_name: "",
    last_name: "",
    street: "",
    zip_code: "",
    city: "",
    country: "",
    email: "",
    home_phone: "",
    accept_contact: false,
    newsletter: false,
    description: "",
    merkmal: "Bewertung",
  });
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);

  // Check if User allready started the form, if not send event to meta pixel

  useEffect(()=>{
    if (!started) {
      trackBewertungStart();
    }
    setStarted(true);
  },[]);

  const handleIncrement = (field) => {
    updateFormData((prevData) => {
      const currentValue = parseInt(prevData[field] || "0", 10);
      return { ...prevData, [field]: (currentValue + 1).toString() };
    });
  };

  const handleDecrement = (field) => {
    updateFormData((prevData) => {
      const currentValue = parseInt(prevData[field] || "0", 10);
      const newValue = Math.max(0, currentValue - 1);
      return { ...prevData, [field]: newValue.toString() };
    });
  };

  const handleSearchTextChange = (value) => {
    setSearchValue(value);
  };

  const formTitles = {
    immobilie: "Immobilienbewertung",
    haus: "Hausbewertung",
    wohnung: "Wohnungsbewertung",
    mehrfamilienhaus: "Mehrfamilienhausbewertung",
  };
  const formTitle = formTitles[type] || "Bewertung";

  // ---------------------------
  // Validation logic (8 steps)
  // ---------------------------
  const getStepSchema = (step) => {
    switch (step) {
      case 1:
      return z.object({
        propertyType: z
        .string()
        .min(1, "Bitte wählen Sie eine Immobilienart."),
      });
      case 2:
      return z.object({
        subPropertyType: z
        .string()
        .min(1, "Bitte wählen Sie eine genaue Immobilienart."),
      });
      case 3:
      if (formData.propertyType === "Wohnung") {
        return z.object({
        balconySize: z
          .string()
          .min(1, "Bitte geben Sie die Größe von Balkon/Terrasse ein."),
        rooms: z
          .string()
          .min(1, "Bitte geben Sie die Anzahl der Zimmer ein."),
        parkingSpaces: z.string().optional(),
        garageSpaces: z.string().optional(),
        });
      } else if (formData.propertyType === "Haus") {
        return z.object({
        balconySize: z
          .string()
          .min(1, "Bitte geben Sie die Größe von Balkon/Terrasse ein."),
        rooms: z
          .string()
          .min(1, "Bitte geben Sie die Anzahl der Zimmer ein."),
        parkingSpaces: z.string().optional(),
        garageSpaces: z.string().optional(),
        basement: z
          .string()
          .min(1, "Bitte wählen Sie, ob unterkellert vorhanden ist."),
        });
      } else if (formData.propertyType === "Gewerbe") {
        if (formData.subPropertyType === "Mehrfamilienhaus") {
        return z.object({
          balconySize: z
          .string()
          .min(1, "Bitte geben Sie die Größe von Balkon/Terrasse ein."),
          rooms: z
          .string()
          .min(1, "Bitte geben Sie die Anzahl der Zimmer ein."),
          parkingSpaces: z.string().optional(),
          garageSpaces: z.string().optional(),
          basement: z
          .string()
          .min(1, "Bitte wählen Sie, ob unterkellert vorhanden ist."),
        });
        } else {
        return z.object({
            city: z.string().min(1, "Bitte wählen Sie eine Stadt aus"),
          street: z.string().min(1, "Bitte geben Sie eine Straße mit Hausnummer an"),
          zip_code: z.string().min(1, "Bitte geben Sie eine PLZ ein"),
            commercialUnits: z
          .string()
          .min(1, "Bitte geben Sie die Anzahl der Einheiten ein."),
          commercialArea: z
          .string()
          .min(1, "Bitte geben Sie die Fläche ein."),
          constructionYear: z
          .string()
          .min(1, "Bitte geben Sie das Baujahr ein."),
        });
        }
      } else if (formData.propertyType === "Grundstück") {
        return z.object({
          city: z.string().min(1, "Bitte wählen Sie eine Stadt aus"),
          street: z.string().min(1, "Bitte geben Sie eine Straße mit Hausnummer an"),
          zip_code: z.string().min(1, "Bitte geben Sie eine PLZ ein"),
        plotSize: z
          .string()
          .min(1, "Bitte geben Sie die Grundstücksfläche ein."),
        });
      }
      return z.object({});
      case 4:
      if (formData.propertyType === "Wohnung") {
        return z.object({
          city: z.string().min(1, "Bitte wählen Sie eine Stadt aus"),
          street: z.string().min(1, "Bitte geben Sie eine Straße mit Hausnummer an"),
          zip_code: z.string().min(1, "Bitte geben Sie eine PLZ ein"),
        constructionYear: z
          .string()
          .min(1, "Bitte geben Sie das Baujahr ein."),
        livingSpace: z
          .string()
          .min(1, "Bitte geben Sie die Wohnfläche ein."),
        });
      } else if (formData.propertyType === "Haus") {
        return z.object({
          city: z.string().min(1, "Bitte wählen Sie eine Stadt aus"),
          street: z.string().min(1, "Bitte geben Sie eine Straße mit Hausnummer an"),
          zip_code: z.string().min(1, "Bitte geben Sie eine PLZ ein"),
        constructionYear: z
          .string()
          .min(1, "Bitte geben Sie das Baujahr ein."),
        livingSpace: z
          .string()
          .min(1, "Bitte geben Sie die Wohnfläche ein."),
        plotSize: z
          .string()
          .min(1, "Bitte geben Sie die Grundstücksfläche ein."),
        });
      }
      return z.object({});
      case 5:
      if (formData.propertyType === "Grundstück") {
        return z.object({
        quality: z
          .string()
          .min(1, "Bitte wählen Sie einen Entwicklungsstand."),
        });
      } else {
        return z.object({
        equipment: z.string().min(1, "Bitte wählen Sie eine Ausstattung."),
        });
      }
      case 6:
      if (formData.propertyType === "Grundstück") {
        return z.object({});
      } else {
        return z.object({
        condition: z.string().min(1, "Bitte wählen Sie den Zustand."),
        });
      }
      case 7:
      const baseSchema = z.object({
        reason: z.string().min(1, "Bitte wählen Sie einen Grund."),
      });

      if (
        formData.reason === "Verkaufsabsicht" ||
        formData.reason === "Kaufabsicht"
      ) {
        return baseSchema.extend({
        timeline: z
          .string()
          .min(1, "Bitte wählen Sie einen zeitlichen Horizont."),
        });
      }
      return baseSchema;
      case 8:
      const contactSchema = z.object({
        first_name: z.string().min(1, "Bitte geben Sie Ihren Vornamen ein."),
        last_name: z.string().min(1, "Bitte geben Sie Ihren Nachnamen ein."),
        email: z
        .string()
        .email("Bitte geben Sie eine gültige E-Mail-Adresse ein."),
        home_phone: z
        .string()
        .min(1, "Bitte geben Sie Ihre Telefonnummer ein."),
        accept_contact: z.boolean().refine((val) => val === true, {
        message: "Bitte akzeptieren Sie die Datenschutzbestimmungen.",
        }),
      });

      try {
        contactSchema.parse(contactData);
        return z.object({});
      } catch (error) {
        const contactErrors = error.errors.reduce((acc, curr) => {
        acc[curr.path[0]] = curr.message;
        return acc;
        }, {});
        setErrors(contactErrors);
        return z.object({});
      }
      default:
      return z.object({});
    }
    };

  const handleSubmit = async () => {
    // --- Simple Contact Validation ---
    const newErrors = {};
    if (!contactData.first_name.trim()) {
      newErrors.first_name = "Bitte geben Sie Ihren Vornamen ein.";
    }
    if (!contactData.last_name.trim()) {
      newErrors.last_name = "Bitte geben Sie Ihren Nachnamen ein.";
    }
    if (!contactData.email.trim()) {
      newErrors.email = "Bitte geben Sie Ihre E-Mail-Adresse ein.";
    } else if (!/\S+@\S+\.\S+/.test(contactData.email)) {
      // Simple email format check
      newErrors.email = "Bitte geben Sie eine gültige E-Mail-Adresse ein.";
    }
    if (!contactData.home_phone.trim()) {
      newErrors.home_phone = "Bitte geben Sie Ihre Telefonnummer ein.";
    }
    if (!contactData.accept_contact) {
      newErrors.accept_contact =
        "Bitte akzeptieren Sie die Datenschutzbestimmungen.";
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return; // Stop submission if there are errors
    }
    // --- End Validation ---

    setErrors({}); // Clear errors if validation passes
    setLoading(true);
    try {
      const payload = { contactData, formData };
      const response = await fetch(`${config.apiUrl}/api/create-object`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (!response.ok) {
        console.error("Error creating object:", data);
        // You could set a general error state here if needed
        setErrors({
          submit: `Fehler: ${data.message || "Bitte versuchen Sie es erneut."}`,
        });
        alert(`Error creating object: ${data.message || "Please try again."}`);
      } else {
        setShowModal(true); // Show success modal
        window.fbq("trackCustom", "BewertungsFormularAbgeschlossen");
      }
    } catch (error) {
      console.error("Error:", error);
      setErrors({
        submit:
          "Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.",
      });
      alert("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const lastParam = window.location.pathname.split("/").pop() || null;
    setLastParam(lastParam);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    updateFormData((prevData) => {
      const updatedData = { ...prevData, [name]: value };
      console.log("Updated formData:", updatedData);
      return updatedData;
    });
  };

  const handleContactInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setContactData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    // Clear the specific error when the user starts typing/checking
    if (errors[name]) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  const handleLocationChange = (location) => {
    updateFormData((prev) => ({
      ...prev,
      city: location.city,
      location: location,
    }));
  };

  const handleCityChange = (city) => {
    updateFormData((prevData) => ({
      ...prevData,
      city: city,
    }));
    console.log("City changed:", city);
  };

  const handleReset = () => {
    updateFormData({
      propertyType: "",
      subPropertyType: "",
      location: "",
      rooms: "",
      city: "",
      constructionYear: "",
      renovationYear: "",
      livingSpace: "",
      plotSize: "",
      floors: "",
      efficiency: "",
      balconySize: "",
      garageSpaces: "",
      parkingSpaces: "",
      condition: "",
      equipment: "",
      reason: "",
      timeline: "",
      basement: "",
      commercialUnits: "",
      commercialArea: "",
      quality: "",
    });
    setContactData({
      first_name: "",
      last_name: "",
      street: "",
      zip_code: "",
      city: "",
      country: "",
      email: "",
      home_phone: "",
      accept_contact: false,
      newsletter: false,
      description: "",
    });
    setStep(1);
    setErrors({});
    setSearchValue("");
  };
  const handleNext = () => {
    
    const schema = getStepSchema(step);
    const validationResult = schema.safeParse(formData);
    console.log("Validation result:", formData);

    if (!validationResult.success) {
      // Gather validation errors
      const errorMap = validationResult.error.errors.reduce((acc, curr) => {
        acc[curr.path[0]] = curr.message;
        return acc;
      }, {});
      setErrors(errorMap);
    } else {
      // Validation passed
      setErrors({});

      // --- 1. Skip step 2 if propertyType === "Wohnung" ---
      if (step === 1 && formData.propertyType === "Wohnung") {
        // Jump from step 1 --> step 3
        setStep(3);
        return;
        // IMPORTANT: return so we don’t continue on to setStep(step+1)
      }

      // --- 2. Existing logic to skip from step 3 -> 5 for Gewerbe/Grundstück ---
      if (step === 3) {
        if (
          (formData.propertyType === "Gewerbe" &&
            formData.subPropertyType !== "Mehrfamilienhaus") ||
          formData.propertyType === "Grundstück"
        ) {
          setStep(5);
          return;
        }
      }

      // --- 3. Existing logic to skip from step 5 -> 7 if "Grundstück" ---
      if (step === 5 && formData.propertyType === "Grundstück") {
        setStep(7);
        return;
      }

      // If none of the skip conditions matched, just go to the next step
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    // 1. If we're on step 3 with propertyType="Wohnung", go back to step 1
    if (step === 3 && formData.propertyType === "Wohnung") {
      setStep(1);
      return;
    }

    // 2. If we’re on step 5 with these property types, go back to step 3
    if (
      step === 5 &&
      (formData.propertyType === "Gewerbe" ||
        formData.propertyType === "Grundstück" ||
        formData.propertyType === "Wohnung")
    ) {
      setStep(3);
      return;
    }

    // 3. If we’re on step 7 and propertyType==="Grundstück", skip step 6 going backward
    if (step === 7 && formData.propertyType === "Grundstück") {
      setStep(5);
      return;
    }

    // Otherwise, just go back one step
    setStep(step - 1);
  };

  const renderProgressBar = () => {
    const progressPercentage = Math.floor((step / totalSteps) * 100);
    return (
      <div className="w-full mb-6">
        <progress
          className="progress w-full progress-primary"
          value={progressPercentage}
          max="100"
        ></progress>
      </div>
    );
  };

  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    if (!value.trim()) return;
    updateFormData((prev) => ({ ...prev, [name]: value }));
    setErrors({});
    if (step === 3) {
      // For "Gewerbe" that are not "Mehrfamilienhaus" OR for "Grundstück", skip to step 5
      if (
        (formData.propertyType === "Gewerbe" &&
          formData.subPropertyType !== "Mehrfamilienhaus") ||
        formData.propertyType === "Grundstück"
      ) {
        setStep(5);
        return;
      }
    } else if (step === 5 && formData.propertyType === "Grundstück") {
      // Skip step 6 for "Grundstück"
      setStep(7);
      return;
    }
    setStep(step + 1);
  };

  // Refactored renderStep using helper components
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div>
            <p className="text-sm text-prime-700">{formTitle}</p>
            <RadioCardGroup
              title="Um welche Immobilienart handelt es sich hier?"
              options={[
                {
                  label: "Wohnung",
                  icon: "/Icons/Abfrage/abfrage-immobilienart-wohnung.svg",
                },
                {
                  label: "Haus",
                  icon: "/Icons/Abfrage/abfrage-immobilienart-haus.svg",
                },
                {
                  label: "Gewerbe",
                  icon: "/Icons/Abfrage/abfrage-immobilienart-gewerbe.svg",
                },
                {
                  label: "Grundstück",
                  icon: "/Icons/Abfrage/abfrage-immobilienart-grundstuck.svg",
                },
              ]}
              name="propertyType"
              selectedValue={formData.propertyType}
              onChange={handleRadioChange}
              error={errors.propertyType}
            />
          </div>
        );
      case 2:
        return (
          <div>
            <p className="text-sm text-prime-700">{formTitle}</p>
            <RadioCardGroup
              title="Wählen Sie die genaue Immobilienart"
              options={
                formData.propertyType === "Haus"
                  ? [
                      {
                        label: "Einfamilienhaus",
                        icon: "/Icons/Abfrage/abfrage-immobilienart-haus.svg",
                      },
                      {
                        label: "Zweifamilienhaus",
                        icon: "/Icons/Abfrage/abfrage-immobilienart-haus-zfh.svg",
                      },
                      {
                        label: "Reihenhaus",
                        icon: "/Icons/Abfrage/abfrage-immobilienart-haus-reihenhaus.svg",
                      },
                      {
                        label: "Doppelhaushälfte",
                        icon: "/Icons/Abfrage/abfrage-immobilienart-haus-dhh.svg",
                      },
                      {
                        label: "Villa",
                        icon: "/Icons/Abfrage/abfrage-immobilienart-haus-villa.svg",
                      },
                      {
                        label: "Mehrfamilienhaus",
                        icon: "/Icons/Abfrage/abfrage-immobilienart-haus-mfh.svg",
                      },
                    ]
                  : formData.propertyType === "Wohnung"
                  ? [
                      {
                        label: "Etagenwohnung",
                        icon: "/icons/etagenwohnung.png",
                      },
                      { label: "Penthouse", icon: "/icons/penthouse.png" },
                      { label: "Maisonette", icon: "/icons/maisonette.png" },
                      {
                        label: "Dachgeschosswohnung",
                        icon: "/icons/dachgeschosswohnung.png",
                      },
                      {
                        label: "Souterrainwohnung",
                        icon: "/icons/souterrainwohnung.png",
                      },
                    ]
                  : formData.propertyType === "Gewerbe"
                  ? [
                      {
                        label: "Einzelhandel",
                        icon: "/Icons/Abfrage/abfrage-immobilienart-gewerbe-einzelhandel.svg",
                      },
                      {
                        label: "Wohn- und Geschäftshaus",
                        icon: "/Icons/Abfrage/abfrage-immobilienart-gewerbe-wug.svg",
                      },
                      {
                        label: "Büro- oder Praxisgebäude",
                        icon: "/Icons/Abfrage/abfrage-immobilienart-gewerbe-buro.svg",
                      },
                      {
                        label: "Mehrfamilienhaus",
                        icon: "/Icons/Abfrage/abfrage-immobilienart-gewerbe-mfh.svg",
                      },
                      {
                        label: "Sonstige",
                        icon: "/Icons/Abfrage/abfrage-immobilienart-gewerbe-sonstige.svg",
                      },
                    ]
                  : formData.propertyType === "Grundstück"
                  ? [
                      {
                        label: "Bauland",
                        icon: "/Icons/Abfrage/abfrage-immobilienart-grundstuck-bauland.svg",
                      },
                      {
                        label: "Ackerland",
                        icon: "/Icons/Abfrage/abfrage-immobilienart-grundstuck-acker.svg",
                      },
                      {
                        label: "Wald und Grünland",
                        icon: "/Icons/Abfrage/abfrage-immobilienart-grundstuck-wald.svg",
                      },
                      {
                        label: "Sonstiges",
                        icon: "/Icons/Abfrage/abfrage-immobilienart-grundstuck-sonstiges.svg",
                      },
                    ]
                  : []
              }
              name="subPropertyType"
              selectedValue={formData.subPropertyType}
              onChange={handleRadioChange}
              error={errors.subPropertyType}
            />
          </div>
        );
      case 3:
        return (
          <div>
            <p className="text-sm text-prime-700">{formTitle}</p>
            <h2 className="text-xl text-prime-950 font-medium mb-6">
              Details zu Ihrer Immobilie (Teil 1)
            </h2>
            {formData.propertyType === "Wohnung" && (
              <div className="space-y-5">
                <InputWithSuffix
                  label="Balkon/Terrasse"
                  name="balconySize"
                  value={formData.balconySize}
                  suffix="m²"
                  onChange={handleInputChange}
                  error={errors.balconySize}
                  placeholder="Balkon/Terrasse"
                />
                <IncrementInput
                  label="Zimmer"
                  name="rooms"
                  value={formData.rooms}
                  onChange={handleInputChange}
                  error={errors.rooms}
                  handleIncrement={handleIncrement}
                  handleDecrement={handleDecrement}
                  placeholder="Zimmer"
                />

                <div className="grid grid-cols-1 gap-4">
                  <IncrementInput
                    label="Außenstellplätze"
                    name="parkingSpaces"
                    value={formData.parkingSpaces}
                    onChange={handleInputChange}
                    error={errors.parkingSpaces}
                    handleIncrement={handleIncrement}
                    handleDecrement={handleDecrement}
                    placeholder="Außenstellplätze"
                  />
                  <IncrementInput
                    label="Garagenstellplätze"
                    name="garageSpaces"
                    value={formData.garageSpaces}
                    onChange={handleInputChange}
                    error={errors.garageSpaces}
                    handleIncrement={handleIncrement}
                    handleDecrement={handleDecrement}
                    placeholder="Garagenstellplätze"
                  />
                </div>
              </div>
            )}
            {formData.propertyType === "Haus" && (
              <div className="space-y-5">
                <InputWithSuffix
                  label="Balkon/Terrasse"
                  name="balconySize"
                  value={formData.balconySize}
                  suffix="m²"
                  onChange={handleInputChange}
                  error={errors.balconySize}
                  placeholder="Balkon/Terrasse"
                />
                <IncrementInput
                  label="Zimmer"
                  name="rooms"
                  value={formData.rooms}
                  onChange={handleInputChange}
                  error={errors.rooms}
                  handleIncrement={handleIncrement}
                  handleDecrement={handleDecrement}
                  placeholder="Zimmer"
                />

                <div className="grid grid-cols-1 gap-4">
                  <IncrementInput
                    label="Außenstellplätze"
                    name="parkingSpaces"
                    value={formData.parkingSpaces}
                    onChange={handleInputChange}
                    error={errors.parkingSpaces}
                    handleIncrement={handleIncrement}
                    handleDecrement={handleDecrement}
                    placeholder="Außenstellplätze"
                  />
                  <IncrementInput
                    label="Garagenstellplätze"
                    name="garageSpaces"
                    value={formData.garageSpaces}
                    onChange={handleInputChange}
                    error={errors.garageSpaces}
                    handleIncrement={handleIncrement}
                    handleDecrement={handleDecrement}
                    placeholder="Garagenstellplätze"
                  />

                  <div className="flex flex-col">
                    <label className="block text-sm font-medium mb-1">
                      Unterkellert?
                    </label>
                    <select
                      name="basement"
                      value={formData.basement}
                      onChange={handleInputChange}
                      className="select select-bordered input-md bg-gray-100 w-full"
                    >
                      <option value="">Bitte wählen</option>
                      <option value="Ja">Ja</option>
                      <option value="Nein">Nein</option>
                    </select>
                    {errors.basement && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.basement}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            {formData.propertyType === "Gewerbe" && (
              <>
                {formData.subPropertyType === "Mehrfamilienhaus" ? (
                  <div className="space-y-5">
                    <InputWithSuffix
                      label="Balkon/Terrasse"
                      name="balconySize"
                      value={formData.balconySize}
                      suffix="m²"
                      onChange={handleInputChange}
                      error={errors.balconySize}
                      placeholder="Balkon/Terrasse"
                    />
                    <IncrementInput
                      label="Zimmer"
                      name="rooms"
                      value={formData.rooms}
                      onChange={handleInputChange}
                      error={errors.rooms}
                      handleIncrement={handleIncrement}
                      handleDecrement={handleDecrement}
                      placeholder="Zimmer"
                    />

                    <div className="grid grid-cols-1 gap-4">
                      <IncrementInput
                        label="Außenstellplätze"
                        name="parkingSpaces"
                        value={formData.parkingSpaces}
                        onChange={handleInputChange}
                        error={errors.parkingSpaces}
                        handleIncrement={handleIncrement}
                        handleDecrement={handleDecrement}
                        placeholder="Außenstellplätze"
                      />
                      <IncrementInput
                        label="Garagenstellplätze"
                        name="garageSpaces"
                        value={formData.garageSpaces}
                        onChange={handleInputChange}
                        error={errors.garageSpaces}
                        handleIncrement={handleIncrement}
                        handleDecrement={handleDecrement}
                        placeholder="Garagenstellplätze"
                      />

                      <div className="flex flex-col">
                        <label className="block text-sm font-medium mb-1">
                          Unterkellert?
                        </label>
                        <select
                          name="basement"
                          value={formData.basement}
                          onChange={handleInputChange}
                          className="select select-bordered input-md bg-gray-100 w-full"
                        >
                          <option value="">Bitte wählen</option>
                          <option value="Ja">Ja</option>
                          <option value="Nein">Nein</option>
                        </select>
                        {errors.basement && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.basement}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <CitySearchBox
                      className="w-full"                        
                    />
                    {errors.street && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.street}
                      </p>
                    )}
                    <IncrementInput
                      label="Wohn- und Gewerbeeinheiten"
                      name="commercialUnits"
                      value={formData.commercialUnits}
                      onChange={handleInputChange}
                      error={errors.commercialUnits}
                      handleIncrement={handleIncrement}
                      handleDecrement={handleDecrement}
                      placeholder="Wohn- und Gewerbeeinheiten"
                    />
                    <InputWithSuffix
                      label="Wohn- und Gewerbefläche"
                      name="commercialArea"
                      value={formData.commercialArea}
                      suffix="m²"
                      onChange={handleInputChange}
                      error={errors.commercialArea}
                      placeholder="Wohn- und Gewerbefläche"
                    />
                    <div>
                      <label
                        htmlFor="constructionYear"
                        className="block text-sm font-medium mb-1"
                      >
                        Baujahr
                      </label>
                      <input
                        type="number"
                        name="constructionYear"
                        placeholder="Baujahr (z.B. 1996)"
                        value={formData.constructionYear}
                        onChange={handleInputChange}
                        className="input input-bordered input-md bg-gray-100 w-full"
                      />
                      {errors.constructionYear && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.constructionYear}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {formData.propertyType === "Grundstück" && (
              <div className="space-y-4">
                <CitySearchBox
                  className="w-full"
                  
                />
                {errors.street && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.street}
                    </p>
                  )}
                
                <InputWithSuffix
                  label="Grundstücksfläche"
                  name="plotSize"
                  value={formData.plotSize}
                  suffix="m²"
                  onChange={handleInputChange}
                  error={errors.plotSize}
                  placeholder="Grundstücksfläche"
                />
              </div>
            )}
          </div>
        );
      case 4:
        return (
          <div>
            <p className="text-sm text-prime-700">{formTitle}</p>
            <h2 className="text-xl text-prime-950 font-medium mb-6">
              Details zu Ihrer Immobilie (Teil 2)
            </h2>
            {formData.propertyType === "Wohnung" && (
              <div className="flex-col flex-wrap flex gap-4">
                <CitySearchBox
                  className="w-full"
                  
                />
                {errors.street && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.street}
                    </p>
                  )}
                
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Baujahr
                  </label>{" "}
                  <input
                    type="number"
                    name="constructionYear"
                    placeholder="z.B. 1996"
                    value={formData.constructionYear}
                    onChange={handleInputChange}
                    className="input input-bordered input-md bg-gray-100 w-full"
                  />
                  {errors.constructionYear && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.constructionYear}
                    </p>
                  )}
                </div>

                <InputWithSuffix
                  label="Wohnfläche"
                  name="livingSpace"
                  value={formData.livingSpace}
                  onChange={handleInputChange}
                  error={errors.livingSpace}
                  placeholder="z.B. 185"
                  suffix="m²"
                />
              </div>
            )}
            {formData.propertyType === "Haus" && (
              <div className="flex-col flex-wrap flex gap-4">
                <CitySearchBox
                  className="w-full"
                  />
                {errors.street && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.street}
                    </p>
                  )}
                
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Baujahr
                  </label>{" "}
                  <input
                    type="number"
                    name="constructionYear"
                    placeholder="z.B. 1996"
                    value={formData.constructionYear}
                    onChange={handleInputChange}
                    className="input input-bordered input-md bg-gray-100 w-full"
                  />
                  {errors.constructionYear && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.constructionYear}
                    </p>
                  )}
                </div>

                <InputWithSuffix
                  label="Wohnfläche"
                  name="livingSpace"
                  value={formData.livingSpace}
                  onChange={handleInputChange}
                  error={errors.livingSpace}
                  placeholder="z.B. 185"
                  suffix="m²"
                />
                <InputWithSuffix
                  label="Grundstücksfläche"
                  name="plotSize"
                  value={formData.plotSize}
                  onChange={handleInputChange}
                  error={errors.plotSize}
                  placeholder="z.B. 355"
                  suffix="m²"
                />
              </div>
            )}
            {formData.propertyType === "Wohnung" || formData.propertyType === "Gewerbe" &&
            formData.subPropertyType === "Mehrfamilienhaus" ? (
              <div className="flex-col flex-wrap flex gap-4">
                <CitySearchBox
                  className="w-full"
                  />
                {errors.street && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.street}
                    </p>
                  )}
                
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Baujahr
                  </label>{" "}
                  <input
                    type="number"
                    name="constructionYear"
                    placeholder="z.B. 1996"
                    value={formData.constructionYear}
                    onChange={handleInputChange}
                    className="input input-bordered input-md bg-gray-100 w-full"
                  />
                  {errors.constructionYear && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.constructionYear}
                    </p>
                  )}
                </div>

                <InputWithSuffix
                  label="Wohnfläche"
                  name="livingSpace"
                  value={formData.livingSpace}
                  onChange={handleInputChange}
                  error={errors.livingSpace}
                  placeholder="z.B. 185"
                  suffix="m²"
                />
                <InputWithSuffix
                  label="Grundstücksfläche"
                  name="plotSize"
                  value={formData.plotSize}
                  onChange={handleInputChange}
                  error={errors.plotSize}
                  placeholder="z.B. 355"
                  suffix="m²"
                />
              </div>
            ) : null}
          </div>
        );
      case 5:
        if (formData.propertyType === "Grundstück") {
          const entwicklungsstandOptions = [
            {
              label: "Nicht entwickelt",
              icon: "/Icons/Abfrage/abfrage-ausstattung-einfach.svg",
            },
            {
              label: "Teilweise entwickelt",
              icon: "/Icons/Abfrage/abfrage-ausstattung-normal.svg",
            },
            {
              label: "Voll entwickelt",
              icon: "/Icons/Abfrage/abfrage-ausstattung-gehoben.svg",
            },
          ];
          return (
            <div>
              <p className="text-sm">{formTitle}</p>
              <RadioCardGroup
                title="Entwicklungsstand"
                options={entwicklungsstandOptions}
                name="quality"
                selectedValue={formData.quality}
                onChange={handleRadioChange}
                error={errors.quality}
              />
            </div>
          );
        } else {
          const ausstattungOptions = [
            {
              label: "Einfach",
              description: "Schlechter Wärmeschutz, 1-fach verglaste Fenster",
              icon: "/Icons/Abfrage/abfrage-ausstattung-einfach.svg",
            },
            {
              label: "Normal",
              description:
                "Baujahrstypische Ausstattungsmerkmale, 2-Fach verglaste Fenster",
              icon: "/Icons/Abfrage/abfrage-ausstattung-normal.svg",
            },
            {
              label: "Gehoben",
              description:
                "Gute Wärmedämmung, 3-Fach verglaste Fenster, Fußbodenheizung",
              icon: "/Icons/Abfrage/abfrage-ausstattung-gehoben.svg",
            },
            {
              label: "Luxuriös",
              description:
                "Hochwertige Dämmung, 3-fach verglaste Fenster, hochwertiger Boden, Fußbodenheizung",
              icon: "/Icons/Abfrage/abfrage-ausstattung-luxuriös.svg",
            },
          ];
          return (
            <div>
              <p className="text-sm">{formTitle}</p>
              <RadioCardGroup
                title="Ausstattung"
                options={ausstattungOptions}
                name="equipment"
                selectedValue={formData.equipment}
                onChange={handleRadioChange}
                error={errors.equipment}
              />
            </div>
          );
        }
      case 6:
        if (formData.propertyType === "Grundstück") {
          return (
            <div>
              <p className="text-sm">{formTitle}</p>
              <h2 className="text-xl text-prime-950 font-medium mb-6">
                Zustand (entfällt bei Grundstück)
              </h2>
              <p className="text-gray-600 text-sm">
                Für Grundstücke ist kein Zustand nötig.
              </p>
            </div>
          );
        } else {
          const zustandOptions = [
            {
              label: "Gut erhalten",
              description:
                "Die Immobilie wurde gut instand gehalten und teilweise modernisiert",
              icon: "/Icons/Abfrage/abfrage-zustand-gut.svg",
            },
            {
              label: "Neu/ kürzlich renoviert",
              description:
                "Die Wohnung wurde vor kurzem gebaut oder kernsaniert",
              icon: "/Icons/Abfrage/abfrage-zustand-neu.svg",
            },
            {
              label: "Renovierungsbedürftig",
              description:
                "Die Nutzung der Immobilie bedarf vorheriger Renovierung",
              icon: "/Icons/Abfrage/abfrage-zustand-renovierung.svg",
            },
          ];
          return (
            <div>
              <p className="text-sm">{formTitle}</p>
              <RadioCardGroup
                title="Zustand"
                options={zustandOptions}
                name="condition"
                selectedValue={formData.condition}
                onChange={handleRadioChange}
                error={errors.condition}
              />
            </div>
          );
        }
      case 7:
        return (
          <div>
            <p className="text-sm text-prime-700">{formTitle}</p>
            <RadioCardGroup
              title="Grund für Ihre Bewertung"
              options={[
                { label: "Verkaufsabsicht" },
                { label: "Kaufabsicht" },
                { label: "Allgemeines Interesse" },
                { label: "Anderer Grund" },
              ]}
              name="reason"
              selectedValue={formData.reason}
              onChange={handleInputChange}
              error={errors.reason}
            />
            {(formData.reason === "Verkaufsabsicht" ||
              formData.reason === "Kaufabsicht") && (
              <>
                <h3 className="text-lg text-prime-950/80 font-medium mt-6 mb-3">
                  Zeitlicher Horizont
                </h3>
                <RadioCardGroup
                  options={[
                    { label: "1-3 Monate" },
                    { label: "4-12 Monate" },
                    { label: "> 12 Monate" },
                  ]}
                  name="timeline"
                  selectedValue={formData.timeline}
                  onChange={handleRadioChange}
                  error={errors.timeline}
                />
              </>
            )}
          </div>
        );
      case 8:
        return (
          <div>
            <p className="text-sm text-prime-700">Abschließender Schritt</p>{" "}
            {/* Use consistent text */}
            <h2 className="text-xl text-prime-950 font-medium mb-6">
              Ihre Kontaktdaten {/* Changed title */}
            </h2>
            {/* Optional: Display general submission error */}
            {errors.submit && (
              <p className="text-red-500 text-sm mb-3 p-2 bg-red-100 rounded">
                {errors.submit}
              </p>
            )}
            <div className="space-y-4">
              <div>
                {" "}
                {/* Wrap input and error */}
                <input
                  type="text"
                  name="first_name"
                  placeholder="Vorname *"
                  value={contactData.first_name}
                  onChange={handleContactInputChange}
                  required
                  className={`input input-bordered input-md bg-gray-100 w-full ${
                    errors.first_name ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.first_name && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.first_name}
                  </p>
                )}
              </div>
              <div>
                {" "}
                {/* Wrap input and error */}
                <input
                  type="text"
                  name="last_name"
                  placeholder="Nachname *"
                  value={contactData.last_name}
                  onChange={handleContactInputChange}
                  required
                  className={`input input-bordered input-md bg-gray-100 w-full ${
                    errors.last_name ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.last_name && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.last_name}
                  </p>
                )}
              </div>
              <div>
                {" "}
                {/* Wrap input and error */}
                <input
                  type="email"
                  name="email"
                  placeholder="E-Mail *"
                  value={contactData.email}
                  onChange={handleContactInputChange}
                  required
                  className={`input input-bordered input-md bg-gray-100 w-full ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>
              <div>
                {" "}
                {/* Wrap input and error */}
                <input
                  type="text" // or type="tel"
                  name="home_phone"
                  placeholder="Telefonnummer *"
                  value={contactData.home_phone}
                  onChange={handleContactInputChange}
                  required
                  className={`input input-bordered input-md bg-gray-100 w-full ${
                    errors.home_phone ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.home_phone && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.home_phone}
                  </p>
                )}
              </div>
              <div>
                {" "}
                {/* Wrap checkbox and error */}
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="accept_contact"
                    checked={contactData.accept_contact}
                    onChange={handleContactInputChange}
                    required
                    className={`checkbox checkbox-primary ${
                      errors.accept_contact
                        ? "border-red-500 ring-1 ring-red-400"
                        : ""
                    }`} // Highlight if error
                  />
                  <span className="text-sm text-slate-600">
                    Ich akzeptiere die Datenschutzbestimmungen *
                  </span>
                </label>
                {errors.accept_contact && (
                  <p className="text-red-500 text-xs mt-1 pl-6">
                    {errors.accept_contact}
                  </p>
                )}{" "}
                {/* Indent error slightly */}
              </div>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="newsletter"
                  checked={contactData.newsletter}
                  onChange={handleContactInputChange}
                  className="checkbox checkbox-primary"
                />
                <span className="text-sm text-slate-600">
                  Newsletter abonnieren (optional)
                </span>
              </label>
              <textarea
                name="description"
                placeholder="Nachricht (optional)"
                value={contactData.description}
                onChange={handleContactInputChange}
                className="textarea bg-gray-100 textarea-bordered w-full border-gray-300" // Ensure consistent border
              />
              <p className="text-xs text-gray-500">* Pflichtfeld</p>{" "}
              {/* Add required field indicator */}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <SuccessModal modal={showModal} />
      <div className="min-h-screen md:min-h-none md:max-h-[calc(100vh - 10rem)] md:overflow-hidden bg-prime-50">
        <NavBar className="relative bg-white/100" />
        <div
          className=" mx-auto max-w-[80rem] px-4 pb-5 mt-8"
          style={{ perspective: "1200px" }}
        >
          <div className=" md:overflow-hidden  mx-auto md:grid grid-cols-2 gap-8">
            <div className="bg-white max-w-[30rem] h-fit   shadow-2xl z-10 rounded-lg p-6">
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className=" shadow-inner md:hidden mb-2 bg-gray-100 text-prime-800 hover:bg-prime-800/80 rounded-lg"
                  >
                   <MapIcon className="size-5 inline"/> Expose anzeigen
                  </Button>
                </DialogTrigger>
                <DialogContent className="w-[95vw] h-[80vh] overflow-hidden px-0 py-0 rounded-lg mx-auto ">
                  
                  <PreviewComponent
                    selectPosition={coordinates}
                    formData={formData}
                    className="w-full  transition-all duration-1000"
                  />
                 
                </DialogContent>
              </Dialog>
              {renderProgressBar()}
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 0 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 0 }}
                  transition={{ duration: 0.3 }}
                  className="min-h-[30rem]"
                >
                  {renderStep()}
                </motion.div>
              </AnimatePresence>
              <div className="flex justify-between mt-6">
                {step > 1 && (
                  <ButtonPrime
                    onClick={handleBack}
                    className="btn  h-5 bg-white border-none  text-prime-800 hover:bg-prime-200"
                  >
                    Zurück
                  </ButtonPrime>
                )}
                {step < 8 ? (
                  <ButtonPrime
                    onClick={handleNext}
                    className="btn  h-5 bg-prime-900 border-none hover:bg-prime-800/80  text-white"
                  >
                    Weiter
                  </ButtonPrime>
                ) : (
                  <LoadingButton
                    loading={loading}
                    onClick={handleSubmit}
                    className="bg-black text-white"
                  >
                    Absenden
                  </LoadingButton>
                )}
              </div>
            </div>
            <div className="bg-gray-50 hidden md:block md:hover:-translate-x-10 transition-all duration-500 ease-out xl:-translate-x-72 xl:hover:-translate-x-56 md:-translate-x-14  z-0 rounded-lg mt-8 md:p-3">
              <PreviewComponent
                selectPosition={coordinates}
                formData={formData}
                className="w-full transition-all duration-1000"
              />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
